// src/pages/task/TaskReadyPage.tsx
// "Ready to work" — tasks with no unresolved blockers, actionable right now.
// Wraps ListReadyTasks.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n, useAuth } from '@react/hooks';
import { Alert, Badge, Button, Card, CardContent, ErrorAlert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import {
  formatEnumLabel,
  getStatusMapping,
  taskStatusMap,
  taskPriorityMap,
  getDisplayName,
  getEntityId,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
} from '@/features/task/logic';

const READY_PAGE_PARAMS = {
  pagination: { limit: 50, cursor: '', direction: 'forward' },
  sort: [],
};

export const TaskReadyPage = observer(() => {
  const store = useTask();
  const auth = useAuth();
  const { t } = useI18n();
  const scope = useScope();

  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => store.ListReadyTasks(READY_PAGE_PARAMS as any);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.productId]);

  const items = store.items as any[];

  const startTask = async (id: string) => {
    setBusyId(id);
    try {
      await store.ChangeTaskStatus(id, { status: 'TASK_STATUS_IN_PROGRESS' } as any);
      await load();
    } catch { /* store handles error */ } finally { setBusyId(null); }
  };

  const assignToMe = async (id: string) => {
    const userId = auth.activeProfileId;
    if (!userId) return;
    setBusyId(id);
    try {
      await store.AssignTask(id, {
        assignee: { entity: { id: userId }, display_name: auth.user?.username || auth.user?.email || '' },
      } as any);
      await load();
    } catch { /* store handles error */ } finally { setBusyId(null); }
  };

  const header = (
    <div>
      <nav className="text-sm mb-3">
        <Link to="/tasks" className="text-muted-foreground hover:text-foreground">{t('task.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('task.ready.title')}</span>
      </nav>
      <div className="flex items-center justify-between gap-4">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('task.ready.title')}</h1>
        <Button className="ml-auto" variant="secondary" size="sm" onClick={load}>
          {t('task.refresh')}
        </Button>
      </div>
    </div>
  );

  if (store.isLoading && (!items || items.length === 0)) {
    return (
      <div className="space-y-6">
        {header}
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />)}
        </div>
      </div>
    );
  }

  if (store.error) {
    return <div className="space-y-6">{header}<ErrorAlert error={store.error} /></div>;
  }

  if (!items || items.length === 0) {
    return (
      <div className="space-y-6">
        {header}
        <Alert variant="info" title={t('task.ready.empty')}>{t('task.ready.emptyHint')}</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {items.map((item: any, idx: number) => {
            const id = getEntityId(item) || idx.toString();
            const status = getTaskStatus(item);
            const priority = getTaskPriority(item);
            const assignee = getTaskAssignee(item);
            const isMine = !!assignee?.entity?.id && assignee.entity.id === auth.activeProfileId;
            const isBusy = busyId === id;
            return (
              <div key={id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted transition-colors">
                <Link to={`/tasks/${id}`} className="text-sm font-medium text-foreground truncate hover:underline min-w-0 flex-1">
                  {item.title || id.substring(0, 8)}
                </Link>
                <span className="flex items-center gap-2 shrink-0">
                  {priority && <Badge variant={getStatusMapping(taskPriorityMap, priority).variant} size="sm">{formatEnumLabel(priority, 'TASK_PRIORITY_')}</Badge>}
                  {status && <Badge variant={getStatusMapping(taskStatusMap, status).variant} size="sm">{formatEnumLabel(status, 'TASK_STATUS_')}</Badge>}
                  <span className="text-xs text-muted-foreground w-24 text-right truncate">{assignee ? getDisplayName(assignee) : t('task.unassigned')}</span>
                  {status !== 'TASK_STATUS_IN_PROGRESS' && (
                    <Button size="sm" variant="secondary" isLoading={isBusy} onClick={() => startTask(id)}>
                      {t('task.ready.start')}
                    </Button>
                  )}
                  {!isMine && (
                    <Button size="sm" variant="ghost" isLoading={isBusy} onClick={() => assignToMe(id)}>
                      {t('task.ready.assignToMe')}
                    </Button>
                  )}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
});

export default TaskReadyPage;
